import { formatCardKey } from '@/ai/utils/helpers'
import { diagramHelpers } from '@/mixins/diagramHelpers'

export const examples = {
  workflowDiagram: [
    { role: 'Customer', description: 'Created order' },
    { role: 'Support', description: 'Resolved issue' },
    { role: 'Supplier', description: 'Scheduled delivery' }
  ],
  commandExampleCreateOrder: [
    { fieldName: 'Customer Name' },
    { fieldName: 'Product Name' },
    { fieldName: 'Delivery Time' },
    { fieldName: 'Delivery Address' },
    { fieldName: 'Quantity' },
    { fieldName: 'Comment' }
  ],
  commandExampleDeliverOrder: [
    { fieldName: 'Supplier' },
    { fieldName: 'Product Name' },
    { fieldName: 'Delivery Time' },
    { fieldName: 'Delivery Address' },
    { fieldName: 'Quantity' },
    { fieldName: 'Shipping Method' }
  ],
  readModelExampleOrder: [
    { fieldName: 'Customer' },
    { fieldName: 'Order' },
    { fieldName: 'Delivery Location' }
  ],
  readModelExampleCustomer: [
    { fieldName: 'Full Name' },
    { fieldName: 'Address' },
    { fieldName: 'Payment Method' }
  ],
  entityExampleCustomer: [
    {
      fieldName: 'Customer ID',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data'],
      primaryKey: true
    },
    {
      fieldName: 'Customer Name',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data']
    },
    {
      fieldName: 'Customer Address',
      tags: '',
      exampleData: ['Example data', 'Example data', 'Example data']
    },
    {
      fieldName: 'Phone Number',
      tags: '',
      exampleData: ['Example data', 'Example data', 'Example data']
    }
  ],
  entityExampleProduct: [
    {
      fieldName: 'Product ID',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data'],
      primaryKey: true
    },
    {
      fieldName: 'Product Name',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data']
    },
    {
      fieldName: 'Product Type',
      tags: 'REQUIRED',
      exampleData: ['Example data', 'Example data', 'Example data']
    },
    {
      fieldName: 'Stock Level',
      tags: '',
      exampleData: ['x', 'y', 'z']
    }
  ]
}

export const workflowDataExample = {
  events: [
    {
      id: 'event_1',
      description: 'Example description 1',
      laneId: 'lane_1',
      parentEventNames: ['start'],
      cards: [
        {
          id: 'card_1',
          description: 'Card 1',
          cardType: {
            id: 'card_type_1',
            title: 'Card type 1'
          }
        },
        {
          id: 'card_2',
          description: 'Card 2',
          cardType: {
            id: 'card_type_2',
            title: 'Card type 2'
          }
        }
      ],
      dataModels: [
        {
          name: 'Write model',
          type: 'Event Story',
          cardId: 'card_1',
          dataFields: [
            {
              name: 'Field 1',
              cardinality: 'one-to-one',
              exampleData: ['Example data', 'Example data', 'Example data'],
              relatedEntityId: 'entity_1'
            },
            {
              name: 'Field 2',
              cardinality: null,
              exampleData: null,
              relatedEntityId: null
            }
          ]
        },
        {
          name: 'Read Model',
          type: 'Read Model',
          cardId: 'card_2',
          dataFields: [
            {
              name: 'Field 1',
              cardinality: null,
              exampleData: ['Example data', 'Example data', 'Example data'],
              relatedEntityId: 'entity_1'
            },
            {
              name: 'Field 2',
              cardinality: null,
              exampleData: null,
              relatedEntityId: null
            }
          ]
        }
      ]
    },
    {
      id: 'event_2',
      description: 'Example description 2',
      laneId: 'lane_2',
      parentEventNames: ['Example description 1'],
      cards: [
        {
          id: 'card_3',
          description: 'Card 3',
          cardType: {
            id: 'card_type_1',
            title: 'Card type 1'
          }
        },
        {
          id: 'card_4',
          description: 'Card 4',
          cardType: {
            id: 'card_type_2',
            title: 'Card type 2'
          }
        }
      ]
    }
  ],
  lanes: [
    { id: 'lane_1', name: 'Lane 1' },
    { id: 'lane_2', name: 'Lane 2' }
  ],
  entities: [
    {
      id: 'entity_1',
      name: 'Entity 1',
      dataFields: [
        {
          name: 'Field 1',
          cardinality: 'one-to-one',
          exampleData: ['Example', 'Example', 'Example'],
          primaryKey: true,
          reference: null,
          relatedEntityId: null,
          isRequired: true
        },
        {
          name: 'Field 2',
          cardinality: 'one-to-one',
          exampleData: ['Example', 'Example', 'Example'],
          primaryKey: false,
          reference: true,
          relatedEntityId: 'entity_2',
          isRequired: false
        }
      ]
    },
    {
      id: 'entity_2',
      name: 'Entity 2',
      dataFields: [
        {
          name: 'Field 1',
          cardinality: 'one-to-one',
          exampleData: ['Example', 'Example', 'Example'],
          primaryKey: true,
          reference: null,
          relatedEntityId: null,
          isRequired: true
        },
        {
          name: 'Field 2',
          cardinality: 'one-to-many',
          exampleData: ['Example', 'Example', 'Example'],
          primaryKey: false,
          reference: true,
          relatedEntityId: 'entity_3',
          isRequired: false
        }
      ]
    }
  ]
}

export const workflowDescriptionExamples = [
  'The incident management process for ITIL version 3',
  'A manufacturing process for outdoor clothing',
  'An order process used by e-commerce platform similar to Amazon',
  'A process for last mile delivery for an electronics retailer',
  'A KYC process for onboarding a large corporate bank customer',
  'A process for ordering home delivery of food',
  'A recruitment process for engineers used by Google',
  'A CRM process used by Salesforce'
]

export const getEventExample = (
  exampleData,
  cardTypes,
  stepNumber = null,
  options = {}
) => {
  const { role, description, commandExample, readModelExample } = exampleData
  const { shouldAddCards = true, shouldAddDataModels = true } = options
  const exampleObj = { role, description }

  if (stepNumber !== null) {
    exampleObj.step = stepNumber + 0.5
  }

  const commandCardType = diagramHelpers.methods.getCommandCardType(cardTypes)
  const queryCardType = diagramHelpers.methods.getQueryCardType(cardTypes)

  if (shouldAddDataModels && commandExample && commandCardType?.generateWithAI) {
    exampleObj.dataFields = commandExample
  }

  if (shouldAddDataModels && readModelExample && queryCardType?.generateWithAI) {
    exampleObj.readModelDataFields = readModelExample
  }

  if (shouldAddCards && cardTypes?.length) {
    cardTypes.forEach((cardType) => {
      if (cardType.generateWithAI && cardType.title) {
        const key = formatCardKey(cardType.title)
        exampleObj[key] = [`Example description for ${cardType.title.toLowerCase()} X`]
      }
    })
  }

  return exampleObj
}
